import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PartnerAlertChannel } from "@consistency-circle/shared";

import { apiClient } from "../../../lib/api";

const channelOptions: { value: PartnerAlertChannel; label: string; hint: string }[] = [
  { value: "PUSH", label: "Push", hint: "Mobile app only" },
  { value: "EMAIL", label: "Email", hint: "Works everywhere" },
  { value: "BOTH", label: "Both", hint: "Push and email" },
];

export function NotificationsSection() {
  const queryClient = useQueryClient();

  const { data: preferences, isPending } = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: () => apiClient.getNotificationPreferences(),
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.updateNotificationPreferences({ partnerAlertsEnabled: enabled }),
    onSuccess: (next) => {
      queryClient.setQueryData(["notification-preferences"], next);
    },
  });

  const channelMutation = useMutation({
    mutationFn: (channel: PartnerAlertChannel) =>
      apiClient.updateNotificationPreferences({ partnerAlertChannel: channel }),
    onSuccess: (next) => {
      queryClient.setQueryData(["notification-preferences"], next);
    },
  });

  const enabled = preferences?.partnerAlertsEnabled ?? false;
  const channel = preferences?.partnerAlertChannel ?? "BOTH";

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Notifications</h2>
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 accent-[var(--primary)]"
            checked={enabled}
            disabled={isPending || toggleMutation.isPending}
            onChange={(event) => toggleMutation.mutate(event.target.checked)}
          />
          <span className="flex-1">
            <span className="block text-sm font-medium">Circle nudges</span>
            <span className="block text-xs text-muted-foreground">
              Get notified when a circle member misses a shared goal. Window-open reminders stay
              per-goal on the Goals page.
            </span>
          </span>
        </label>

        {enabled ? (
          <div className="flex items-center gap-2 pl-7">
            {channelOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => channelMutation.mutate(option.value)}
                disabled={channelMutation.isPending}
                title={option.hint}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  channel === option.value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                } disabled:opacity-40`}
              >
                {option.label}
              </button>
            ))}
            <span className="text-xs text-muted-foreground">
              {channelOptions.find((option) => option.value === channel)?.hint}
            </span>
          </div>
        ) : null}
      </div>
    </section>
  );
}
